import React, { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { itemSizeOptions } from "@/lib/menuItemSizes";
import { displayItemName } from "@/lib/menuLocale";
import { useCurrency } from "@/hooks/useCurrency";
import { money2 } from "@/lib/format";

export default function PosSizePickerPopup({ open, item, pending, onPick, onClose }) {
  const { code: currencyCode } = useCurrency();
  const sizes = itemSizeOptions(item);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  return (
    <div
      className="fixed inset-0 z-[190] flex items-center justify-center bg-brand-brown/35 backdrop-blur-[2px] p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-size-title"
      data-testid="pos-size-picker"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[420px] bg-[#EFE6DE] rounded-xl border border-[#D7C4B6] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2.5 flex items-center justify-between bg-[#F7EFE8] border-b border-[#E0D0C4]">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-brown/45">Choose size</div>
            <h2 id="pos-size-title" className="text-[15px] font-black text-brand-brown truncate">
              {displayItemName(item)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-white border border-[#E0D0C4] grid place-items-center text-brand-brown hover:bg-[#FFF3E8]"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {sizes.map((s) => (
            <button
              key={s.key}
              type="button"
              disabled={pending}
              data-testid={`pos-size-${s.key}`}
              onClick={() => onPick(s.key)}
              className={cn(
                "rounded-xl border-2 bg-white px-3 py-4 text-center touch-manipulation",
                "hover:border-brand-orange hover:bg-[#FFF3E8] active:translate-y-px",
                "disabled:opacity-50",
              )}
            >
              <div className="text-[13px] font-black text-brand-brown">{s.label}</div>
              <div className="mt-1 font-mono text-[18px] font-black tabular-nums text-brand-orange">
                {money2(s.price)}
              </div>
              <div className="text-[9px] font-bold text-brand-brown/40">{currencyCode}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
